/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React from 'react'
/** @jsx jsx */
import {jsx, css} from '@emotion/react'
import PropTypes from 'prop-types'
import {Box, Heading} from '@salesforce/retail-react-app/app/components/shared/ui'

/**
 * PDC Title component
 * @param {string} title - Title text
 * @param {string} titleHeadingLevel - Title font size
 * @param {string} titleFontColor - Title font color
 * @param {string} txtHorizontalPos - Text horizontal position
 * @param {string} textColor - Text color
 * @param {string} size - Text size (sm, md, lg, ...)
 * @param {string} fontSize - Text font size (in px)
 * @param {string} id - Id for the title
 * @returns {React.ReactElement} - PDC Title component
 */
const PDCTitle = ({
    title,
    titleHeadingLevel,
    titleFontColor,
    txtHorizontalPos,
    textColor,
    size,
    fontSize,
    id,
    ...props
}) => {
    return (
        <Box {...props}>
            {titleHeadingLevel && ['h1', 'h2', 'h3'].includes(titleHeadingLevel) && (
                <Heading
                    dangerouslySetInnerHTML={{
                        __html: title
                    }}
                    as={titleHeadingLevel ? titleHeadingLevel : 'h2'}
                    textAlign={txtHorizontalPos}
                    size={
                        size ||
                        (titleHeadingLevel.slice(-1) === '1'
                            ? '6xl'
                            : titleHeadingLevel.slice(-1) === '2'
                            ? '5xl'
                            : '4xl')
                    }
                    fontSize={fontSize}
                    color={titleFontColor ? titleFontColor : textColor ? textColor : 'fullBlack'}
                    id={id}
                    className="pdc-title"
                />
            )}
            {titleHeadingLevel &&
                ['aria-level1', 'aria-level2', 'aria-level3'].includes(titleHeadingLevel) && (
                    <Box
                        dangerouslySetInnerHTML={{
                            __html: title
                        }}
                        fontFamily="heading"
                        fontSize={
                            fontSize ||
                            size ||
                            (titleHeadingLevel.slice(-1) === '1'
                                ? '6xl'
                                : titleHeadingLevel.slice(-1) === '2'
                                ? '5xl'
                                : '4xl')
                        }
                        lineHeight="1"
                        textAlign={txtHorizontalPos}
                        role="heading"
                        aria-level={titleHeadingLevel.slice(-1)}
                        color={
                            titleFontColor ? titleFontColor : textColor ? textColor : 'fullBlack'
                        }
                        id={id}
                        className="pdc-title"
                    />
                )}
        </Box>
    )
}

PDCTitle.css = css`
    &.sr-only {
        position: absolute;
        left: -10000px;
        top: auto;
        width: 1px;
        height: 1px;
        overflow: hidden;
    }
`

PDCTitle.displayName = 'PDCTitle'

PDCTitle.propTypes = {
    title: PropTypes.string,
    titleHeadingLevel: PropTypes.string,
    titleFontColor: PropTypes.string,
    textColor: PropTypes.string,
    txtHorizontalPos: PropTypes.string,
    id: PropTypes.string,
    size: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.array]),
    fontSize: PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.array])
}

export default PDCTitle
